<template>
  <Teleport v-if="toolbarTarget" :to="toolbarTarget">
    <div class="do-menu">
      <button
        class="do-btn"
        :class="{ 'do-btn--active': visible }"
        @click="toggleVisible"
        title="Atalho [D] para alternar as ordens de desova no gráfico"
      >
        <span class="shortcut">[D]</span>
        Desova: <strong>{{ visible ? 'ON' : 'OFF' }}</strong>
        <span v-if="visible && snapshot" class="do-btn__hint">
          {{ snapshot.plottedCount }}/{{ snapshot.totalCount }}
        </span>
        <span v-if="showFirstSpread" class="do-btn__hint">1ª</span>
        <span v-if="showRestSpreads" class="do-btn__hint do-btn__hint--rest">+</span>
      </button>

      <div class="do-menu__panel">
        <div
          class="do-field"
          title="Plota só as desovas cujo target está a até este valor do preço atual. 0 = todas."
        >
          <label class="do-field__lbl">Range:</label>
          <input
            type="number"
            v-model.number="rangeUsd"
            step="100"
            min="0"
            max="50000"
            class="do-field__input"
          />
          <span class="do-field__unit">USD</span>
        </div>
        <p class="do-range-help">
          0 = todo o livro · senão só ±range do preço
        </p>

        <div class="do-toggle-row">
          <span class="do-toggle-row__label">Spread 1ª compra/venda</span>
          <button
            type="button"
            class="do-switch"
            :class="{ 'do-switch--on': showFirstSpread }"
            role="switch"
            :aria-checked="showFirstSpread"
            @click="toggleFirstSpread"
            title="Distância em USD e % entre a primeira compra e a primeira venda"
          >
            <span class="do-switch__opt" :class="{ 'is-active': !showFirstSpread }">OFF</span>
            <span class="do-switch__opt" :class="{ 'is-active': showFirstSpread }">ON</span>
          </button>
        </div>

        <div class="do-toggle-row">
          <span class="do-toggle-row__label">Spreads demais pares</span>
          <button
            type="button"
            class="do-switch do-switch--rest"
            :class="{ 'do-switch--on': showRestSpreads }"
            role="switch"
            :aria-checked="showRestSpreads"
            @click="toggleRestSpreads"
            title="Distância percentual entre cada compra e cada venda restantes"
          >
            <span class="do-switch__opt" :class="{ 'is-active': !showRestSpreads }">OFF</span>
            <span class="do-switch__opt" :class="{ 'is-active': showRestSpreads }">ON</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <div v-if="visible && ready" class="desova-orders__overlay">
    <div
      v-for="band in renderedSpreads"
      :key="'spread-' + band.key"
      class="do-spread"
      :class="band.isFirst ? 'do-spread--first' : 'do-spread--rest'"
      :style="{ top: band.top + 'px', height: band.height + 'px' }"
    >
      <div class="do-spread__label">
        <span v-if="band.isFirst" class="do-spread__usd">${{ formatUsd(band.dollarUsd) }}</span>
        <span class="do-spread__pct">{{ formatPct(band.pct) }}</span>
      </div>
    </div>

    <div
      v-for="row in renderedRows"
      :key="row.key"
      class="do-row"
      :class="row.action === 'buy' ? 'do-row--buy' : 'do-row--sell'"
      :style="{ top: row.y + 'px' }"
      @mouseenter="hoveredKey = row.key"
      @mouseleave="hoveredKey = null"
    >
      <div class="do-row__line"></div>
      <div
        class="do-row__label"
        :style="{
          left: labelLeft,
          transform: 'translateY(calc(-50% + ' + row.labelShift + 'px))',
        }"
      >
        <span class="do-row__action">{{ row.action === 'sell' ? 'SELL' : 'BUY' }}</span>
        <span class="do-row__qty">{{ formatQty(row.qty) }}</span>
        <span class="do-row__price">${{ formatUsd(row.targetPrice) }}</span>
        <span v-if="row.unitCount > 1" class="do-row__count">×{{ row.unitCount }}</span>
      </div>

      <div
        v-if="hoveredKey === row.key"
        class="do-tooltip"
        :style="{
          left: labelLeft,
          transform: 'translateY(calc(-50% + ' + row.labelShift + 'px))',
        }"
      >
        <div class="do-tooltip__title">
          {{ row.action === 'sell' ? 'Venda de Spot' : 'Recompra de Short' }}
        </div>
        <div class="do-tooltip__meta">
          {{ formatQty(row.qty) }} BTC
          <span class="do-tooltip__sep">·</span>
          {{ row.unitCount }} unit{{ row.unitCount === 1 ? '' : 's' }}
        </div>
        <div class="do-tooltip__meta">
          Target ${{ formatUsd(row.targetPrice) }}
        </div>
        <ul class="do-tooltip__units">
          <li v-for="unit in row.orders" :key="unit.id">
            {{ formatQty(unit.qty) }} @ ${{ formatUsd(unit.entryPrice) }}
            → ${{ formatUsd(unit.targetPrice) }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useDesovaOrders } from './useDesovaOrders'

const LABEL_GAP_PX = 18
const SPREAD_COL_PX = 100

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  priceToY: { type: Function, required: true },
  currentPrice: { type: Number, default: 0 },
  ready: { type: Boolean, default: false },
  toolbarTarget: { type: [Object, String], default: null },
})

const hoveredKey = ref(null)
const renderedRows = ref([])
const renderedSpreads = ref([])

const {
  visible,
  rangeUsd,
  showFirstSpread,
  showRestSpreads,
  toggleVisible,
  toggleFirstSpread,
  toggleRestSpreads,
  snapshotAt,
} = useDesovaOrders()

const snapshot = computed(() => {
  if (!visible.value || !props.ready) return null
  return snapshotAt(props.currentPrice)
})

const showingSpreads = computed(() => {
  const spreads = snapshot.value?.spreads
  if (!spreads) return false
  return (showFirstSpread.value && spreads.first) || (showRestSpreads.value && spreads.rest.length > 0)
})

const labelLeft = computed(() => (showingSpreads.value ? SPREAD_COL_PX + 8 + 'px' : '8px'))

function formatQty(value) {
  return Number(value || 0).toFixed(4)
}

function formatUsd(value) {
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

function formatPct(value) {
  return Number(value || 0).toFixed(2) + '%'
}

function shiftOverlappingLabels(rows) {
  const sorted = [...rows].sort((a, b) => a.y - b.y)
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const prevCenter = prev.y + prev.labelShift
    const minCenter = prevCenter + LABEL_GAP_PX
    if (curr.y + curr.labelShift < minCenter) {
      curr.labelShift = minCenter - curr.y
    }
  }
  return rows
}

function visibleSpreads(data) {
  const spreads = data?.spreads
  if (!spreads) return []
  const out = []
  if (showFirstSpread.value && spreads.first) out.push(spreads.first)
  if (showRestSpreads.value) out.push(...spreads.rest)
  return out
}

function updateOverlayCoordinates() {
  const data = snapshot.value
  if (!data || typeof props.priceToY !== 'function') {
    renderedRows.value = []
    renderedSpreads.value = []
    return
  }

  const rows = []
  for (const cluster of data.clusters) {
    const y = props.priceToY(cluster.targetPrice)
    if (y == null) continue
    rows.push({
      ...cluster,
      y,
      labelShift: 0,
    })
  }
  renderedRows.value = shiftOverlappingLabels(rows)

  const bands = []
  for (const spread of visibleSpreads(data)) {
    const fromPrice = spread.priceFrom != null ? spread.priceFrom : spread.buyPrice
    const toPrice = spread.priceTo != null ? spread.priceTo : spread.sellPrice
    const y0 = props.priceToY(fromPrice)
    const y1 = props.priceToY(toPrice)
    if (y0 == null || y1 == null) continue
    const height = Math.abs(y1 - y0)
    if (height < 2) continue
    bands.push({
      ...spread,
      top: Math.min(y0, y1),
      height,
    })
  }
  renderedSpreads.value = bands
}

let rafId = null
function startSyncLoop() {
  function loop() {
    if (visible.value && props.ready) {
      updateOverlayCoordinates()
    }
    rafId = requestAnimationFrame(loop)
  }
  loop()
}

function stopSyncLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch([snapshot, showFirstSpread, showRestSpreads], updateOverlayCoordinates)

onMounted(() => {
  startSyncLoop()
})

onUnmounted(() => {
  stopSyncLoop()
})
</script>

<style scoped>
.do-menu {
  position: relative;
}

.do-menu__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 240px;
  padding: 10px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  flex-direction: column;
  gap: 8px;
}

.do-menu__panel::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.do-menu:hover .do-menu__panel,
.do-menu:focus-within .do-menu__panel {
  display: flex;
}

.do-field {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #21262d;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 3px 8px;
}

.do-field__lbl {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.do-field__input {
  width: 64px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #58a6ff;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  padding: 2px 0;
}

.do-field__input:focus {
  outline: none;
  border-color: #58a6ff;
}

.do-field__unit {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.do-range-help {
  margin: 0;
  padding: 6px 8px;
  background: rgba(227, 179, 65, 0.08);
  border: 1px solid rgba(227, 179, 65, 0.35);
  border-radius: 6px;
  color: #e3b341;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
}

.do-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.do-toggle-row__label {
  font-size: 12px;
  font-weight: 600;
  color: #c9d1d9;
}

.do-switch {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 999px;
  cursor: pointer;
}

.do-switch--on {
  border-color: #58a6ff;
}

.do-switch--rest.do-switch--on {
  border-color: #a371f7;
}

.do-switch__opt {
  min-width: 36px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  color: #8b949e;
  text-align: center;
}

.do-switch__opt.is-active {
  background: #30363d;
  color: #f0f6fc;
}

.do-switch--on .do-switch__opt.is-active {
  background: rgba(88, 166, 255, 0.2);
  color: #58a6ff;
}

.do-switch--rest.do-switch--on .do-switch__opt.is-active {
  background: rgba(163, 113, 247, 0.2);
  color: #a371f7;
}

.do-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.do-btn:hover {
  background: #30363d;
  border-color: #8b949e;
}

.do-btn--active {
  background: rgba(227, 179, 65, 0.18);
  color: #e3b341;
  border-color: #e3b341;
}

.do-btn__hint {
  margin-left: 2px;
  background: rgba(88, 166, 255, 0.2);
  color: #58a6ff;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid #58a6ff;
}

.do-btn__hint--rest {
  background: rgba(163, 113, 247, 0.2);
  color: #a371f7;
  border-color: #a371f7;
}

.shortcut {
  color: #8b949e;
  font-weight: 400;
  font-size: 11px;
}

.desova-orders__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 6;
  overflow: visible;
}

.do-spread {
  position: absolute;
  left: 0;
  width: 100px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-left: 2px solid;
}

.do-spread--first {
  background: rgba(88, 166, 255, 0.18);
  border-left-color: #58a6ff;
  z-index: 2;
}

.do-spread--rest {
  background: rgba(163, 113, 247, 0.16);
  border-left-color: #a371f7;
  border-top: 1px solid rgba(163, 113, 247, 0.45);
  border-bottom: 1px solid rgba(163, 113, 247, 0.45);
  z-index: 1;
}

.do-spread__label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.85);
}

.do-spread__usd {
  font-size: 11px;
  font-weight: 800;
  color: #f0f6fc;
}

.do-spread--first .do-spread__pct {
  font-size: 12px;
  font-weight: 800;
  color: #79c0ff;
}

.do-spread--rest .do-spread__pct {
  font-size: 10px;
  font-weight: 700;
  color: #d2a8ff;
}

.do-row {
  position: absolute;
  left: 0;
  right: 72px;
  height: 0;
  pointer-events: none;
}

.do-row__line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed;
  opacity: 0.85;
}

.do-row--buy .do-row__line {
  border-top-color: #3fb950;
}

.do-row--sell .do-row__line {
  border-top-color: #f85149;
}

.do-row__label {
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  pointer-events: auto;
  cursor: default;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  z-index: 3;
}

.do-row--buy .do-row__label {
  background: rgba(46, 160, 67, 0.22);
  border: 1px solid rgba(63, 185, 80, 0.7);
  color: #3fb950;
}

.do-row--sell .do-row__label {
  background: rgba(248, 81, 73, 0.22);
  border: 1px solid rgba(248, 81, 73, 0.7);
  color: #f85149;
}

.do-row__action {
  font-weight: 800;
}

.do-row__qty,
.do-row__price {
  color: #f0f6fc;
  font-weight: 600;
}

.do-row__count {
  opacity: 0.8;
}

.do-tooltip {
  position: absolute;
  top: 18px;
  min-width: 210px;
  padding: 8px 10px;
  background: #1e2430;
  border: 1px solid #4b5563;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  color: #c9d1d9;
  font-size: 11px;
  pointer-events: none;
  z-index: 25;
}

.do-row--buy .do-tooltip {
  border-color: #2ea043;
}

.do-row--sell .do-tooltip {
  border-color: #da3633;
}

.do-tooltip__title {
  font-weight: 800;
  color: #f0f6fc;
  margin-bottom: 4px;
}

.do-tooltip__meta {
  font-size: 11px;
  font-weight: 600;
  color: #c9d1d9;
}

.do-tooltip__sep {
  color: #8b949e;
  margin: 0 4px;
}

.do-tooltip__units {
  margin: 6px 0 0;
  padding-left: 14px;
  color: #8b949e;
  font-weight: 600;
}

.do-tooltip__units li {
  margin: 2px 0;
}
</style>
